import { useEffect, useState, useMemo } from "react";

import {
  currencyToReadable,
  isValidSingleStatQuery,
} from "../../utils/DashboardUtils";
import useQueriedRecords from "../../api/api-hooks/records/useQueriedRecords";
import useFieldMap from "../../hooks/useFieldMap";
import WidgetLoading from "./WidgetLoading";
import WidgetError from "./WidgetError";

interface Props {
  section: any;
  setSettings: any;
  record: any;
  topLevelFilters: any;
  viewFilters: any;
}

function formatNumber(input: any) {
  if (typeof input !== "number") return input;

  if (Number.isInteger(input)) {
    return input.toString();
  }

  return input.toFixed(2);
}

export default function SingleStatWidget({
  section,
  setSettings,
  record,
  topLevelFilters,
  viewFilters,
}: Props) {
  const [title, setTitle] = useState("");

  const variableValues = useMemo(() => {
    let vals: Record<string, any> = {};

    if (record) {
      vals["record"] = record;
    }

    return vals;
  }, [record]);

  const { records, recordsLoading } = useQueriedRecords({
    variableValues: variableValues,
    moduleAPIName: section?.ModuleAPIName,
    query: section?.Query,
    queryValidator: isValidSingleStatQuery,
    useDefaultVariables: true,
    topLevelFilters: topLevelFilters,
    viewFilters: viewFilters,
    queryKeyID: section?.id,
  });
  const fieldMap = useFieldMap(section?.ModuleAPIName);

  const aggregationMethod = section?.Query?.Aggregations?.[0]?.Method;
  const aggregationField = section?.Query?.Aggregations?.[0]?.Field;
  const aggregationFieldSchema = fieldMap?.[aggregationField];
  const aggregationFieldType = aggregationFieldSchema?.FieldType;
  const stat = records?.[0]?.[aggregationMethod]?.[aggregationField];

  useEffect(() => {
    if (!stat) {
      setTitle("--");
      return;
    }

    if (
      aggregationFieldType === "CurrencyInterval" ||
      aggregationFieldType === "Currency" ||
      aggregationFieldSchema?.CalculatedFieldData?.Output === "currency"
    ) {
      setTitle(currencyToReadable(stat));
      return;
    }

    const finalStat = formatNumber(stat);

    if (
      aggregationFieldType === "Percent" ||
      aggregationFieldSchema?.CalculatedFieldData?.Output === "currency"
    ) {
      setTitle(finalStat + "%");
      return;
    }

    setTitle(typeof finalStat === "object" ? "--" : finalStat);
  }, [
    aggregationMethod,
    aggregationField,
    stat,
    aggregationFieldType,
    aggregationFieldSchema,
  ]);

  // --------------- No Records Found State ---------------
  if (!isValidSingleStatQuery(section?.Query)) {
    return (
      <WidgetError
        onOpenSettings={setSettings ? () => setSettings(section) : null}
      />
    );
  }

  // ------------------ Loading State ----------------------
  if (recordsLoading) {
    return <WidgetLoading />;
  }

  const noResults = !records || !records.length;

  return (
    <div
      style={{ height: "100%", width: "100%" }}
      className="overflow-hidden pt-3"
    >
      <div
        style={{
          width: "100%",
          height: "100%",
        }}
        className="d-flex justify-content-center align-items-center"
      >
        <svg viewBox="0 2 108.5 29" xmlns="http://www.w3.org/2000/svg">
          <foreignObject x="0" y="0" width="100%" height="100%">
            <div
              style={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {noResults ? "0" : title}
            </div>
          </foreignObject>
        </svg>
      </div>
    </div>
  );
}
